import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  usersUrl, userStatusUrl, userResetPwdUrl, userPermsUrl,
  rolesUrl, roleByIdUrl, rolePermissionsUrl, permissionsUrl, branchesUrl,
  attendanceLinkUrl, attendanceDevicesUrl,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtDate, titleCase } from '../../utils/format';
import { toastSuccess, swalSuccess, swalWarning, swalDangerConfirm, showAxiosError } from '../../utils/swal';

const emptyUser = {
  full_name: '', username: '', email: '', phone: '',
  branch_id: '', role: 'cashier', temp_password: '',
  device_serial: '', device_user_id: '',
};

const generatePassword = () => {
  const a = 'ABCDEFGHJKMNPQRSTUVWXYZ';
  const b = 'abcdefghijkmnpqrstuvwxyz';
  const n = '23456789';
  const s = '!@#$&*';
  const pool = (a + b + n + s);
  let out = '';
  for (let i = 0; i < 12; i++) out += pool[Math.floor(Math.random() * pool.length)];
  return out;
};

const BRANCH_MGR_ROLES = new Set(['cashier', 'store_officer', 'accountant']);

// Deterministic colored avatar per role (same role → same colors across reloads).
const ROLE_AVATAR_COLORS = [
  { bg: '#e0e7ff', fg: '#3730a3' },
  { bg: '#dbeafe', fg: '#1d4ed8' },
  { bg: '#cffafe', fg: '#0e7490' },
  { bg: '#d1fae5', fg: '#047857' },
  { bg: '#fef3c7', fg: '#b45309' },
  { bg: '#ffedd5', fg: '#c2410c' },
  { bg: '#fce7f3', fg: '#be185d' },
  { bg: '#e9d5ff', fg: '#7e22ce' },
];
const avatarFor = (key = '') => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffffff;
  return ROLE_AVATAR_COLORS[Math.abs(h) % ROLE_AVATAR_COLORS.length];
};
const roleInitials = (name = '') => String(name).split(/\s+/).filter(Boolean).slice(0, 2)
  .map((p) => p[0].toUpperCase()).join('') || '?';

// Friendly icons per permission category. Falls back to a gear for unknown ones.
const CAT_ICONS = {
  sales: '🛒', inventory: '📦', purchase: '📥', purchases: '📥',
  finance: '💰', admin: '⚙️', catalog: '🏷️', auth: '🔐', other: '🔧',
};
const catIcon = (k) => CAT_ICONS[String(k || '').toLowerCase()] || '🔧';

const UsersAccess = () => {
  const me = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } })();
  const isSuper = me?.role === 'super_admin';

  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyUser, branch_id: isSuper ? '' : (me?.branch_id || '') });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [permModalUser, setPermModalUser] = useState(null);
  const [userPerms, setUserPerms] = useState({ role_permissions: [], overrides: [], effective: [] });
  const [pendingGrants, setPendingGrants] = useState(new Set());

  // ─── Roles tab state ────────────────────────────────────────
  const [tab, setTab] = useState('users'); // 'users' | 'roles'
  const [roleSearch, setRoleSearch] = useState('');
  const [roleModal, setRoleModal] = useState(null); // null | { mode: 'create' | 'edit', role }
  const [roleForm, setRoleForm] = useState({ role_key: '', display_name: '', description: '' });
  const [rolePerms, setRolePerms] = useState(new Set());
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const [permFilter, setPermFilter] = useState('');
  const [copyFromRole, setCopyFromRole] = useState('');
  const [collapsedCats, setCollapsedCats] = useState(new Set());

  // ─── Create-user permission picker (super_admin only) ───────
  const [userPermsPick, setUserPermsPick] = useState(new Set());
  const [userPermFilter, setUserPermFilter] = useState('');
  const [userPermCollapsed, setUserPermCollapsed] = useState(new Set());
  const [userPermsTouched, setUserPermsTouched] = useState(false);

  // ─── Device / fingerprint enrollment ────────────────────────
  const [devices, setDevices] = useState([]);
  const [deviceModal, setDeviceModal] = useState(null); // { user } | null
  const [deviceForm, setDeviceForm] = useState({ device_serial: '', device_user_id: '' });
  const [deviceSaving, setDeviceSaving] = useState(false);
  const [deviceError, setDeviceError] = useState(null);

  const load = async () => {
    const [u, b, r, p, d] = await Promise.all([
      axios.get(usersUrl), axios.get(branchesUrl), axios.get(rolesUrl), axios.get(permissionsUrl),
      axios.get(attendanceDevicesUrl).catch(() => ({ data: { results: [] } })),
    ]);
    setUsers(u.data?.results || []);
    setBranches(b.data?.results || []);
    setRoles(r.data?.results || []);
    setPerms(p.data?.results || []);
    setDevices(d.data?.results || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  // Whenever the role changes inside the Create User modal, refresh the
  // permission picker to match the new role's defaults — unless the admin
  // has already manually tweaked it (so we don't blow away their edits).
  useEffect(() => {
    if (!open || !isSuper) return;
    if (userPermsTouched) return;
    const r = roles.find((x) => x.role_key === form.role);
    setUserPermsPick(new Set(r?.permissions || []));
  }, [form.role, open, isSuper, roles, userPermsTouched]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter((u) =>
      [u.full_name, u.username, u.email, u.role, u.branch_name].some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [users, search]);

  const permsByCategory = useMemo(() => {
    const by = {};
    perms.forEach((p) => { (by[p.category] ||= []).push(p); });
    return by;
  }, [perms]);

  const filteredRoles = useMemo(() => {
    if (!roleSearch.trim()) return roles;
    const q = roleSearch.trim().toLowerCase();
    return roles.filter((r) =>
      [r.display_name, r.role_key, r.description].some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [roles, roleSearch]);

  // Two counts per role: total assignments and ACTIVE assignments.
  // Only active users block role deletion — suspended accounts are out of circulation.
  const roleUserCounts = useMemo(() => {
    const m = new Map();
    users.forEach((u) => {
      const cur = m.get(u.role) || { total: 0, active: 0 };
      cur.total += 1;
      if (u.is_active === 1 || u.is_active === true) cur.active += 1;
      m.set(u.role, cur);
    });
    return m;
  }, [users]);

  const usersByRole = useMemo(() => {
    const m = new Map();
    users.forEach((u) => {
      if (!m.has(u.role)) m.set(u.role, []);
      m.get(u.role).push(u);
    });
    return m;
  }, [users]);

  // ─── Role helpers ──────────────────────────────────────────
  const openCreateRole = () => {
    setRoleForm({ role_key: '', display_name: '', description: '' });
    setRolePerms(new Set());
    setRoleError(null);
    setPermFilter('');
    setCopyFromRole('');
    setCollapsedCats(new Set()); // start with everything visible for create
    setRoleModal({ mode: 'create' });
  };

  const openEditRole = (role) => {
    if (role.role_key === 'super_admin') {
      return swalSuccess('Super administrator', 'This role has implicit access to everything and cannot be edited here.');
    }
    setRoleForm({ role_key: role.role_key, display_name: role.display_name, description: role.description || '' });
    setRolePerms(new Set(role.permissions || []));
    setRoleError(null);
    setPermFilter('');
    setCopyFromRole('');
    // When editing, collapse categories with NO permissions selected so the
    // admin focuses on what's already granted. Categories with grants stay open.
    const collapse = new Set();
    Object.entries(permsByCategory).forEach(([cat, list]) => {
      const anyOn = list.some((p) => (role.permissions || []).includes(p.perm_key));
      if (!anyOn) collapse.add(cat);
    });
    setCollapsedCats(collapse);
    setRoleModal({ mode: 'edit', role });
  };

  const toggleCatCollapse = (cat) => {
    setCollapsedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const collapseAllCats = () => {
    setCollapsedCats(new Set(Object.keys(permsByCategory)));
  };
  const expandAllCats = () => {
    setCollapsedCats(new Set());
  };

  // Copy the permissions of an existing role into the current selection.
  // Useful when creating a new role that's a variation of an existing one.
  const applyCopyFrom = (roleKey) => {
    setCopyFromRole(roleKey);
    if (!roleKey) return;
    const src = roles.find((r) => r.role_key === roleKey);
    if (!src) return;
    setRolePerms(new Set(src.permissions || []));
  };

  const permFilterMatch = (p) => {
    if (!permFilter.trim()) return true;
    const q = permFilter.trim().toLowerCase();
    return p.perm_key.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
  };

  const saveRole = async () => {
    setRoleError(null);
    if (!roleForm.display_name.trim()) { setRoleError('Display name is required'); return; }
    if (roleModal.mode === 'create' && !roleForm.role_key.trim()) {
      setRoleError('Role key is required (used internally; lowercase letters + underscores)');
      return;
    }

    setRoleSaving(true);
    try {
      if (roleModal.mode === 'create') {
        const res = await axios.post(rolesUrl, {
          role_key: roleForm.role_key,
          display_name: roleForm.display_name,
          description: roleForm.description,
          permissions: Array.from(rolePerms),
        });
        if (!res.data?.success) { setRoleError(res.data?.message || 'Failed'); setRoleSaving(false); return; }
        await load();
        setRoleModal(null);
        toastSuccess(`Role "${roleForm.display_name}" created`);
      } else {
        await axios.patch(roleByIdUrl(roleModal.role.id), {
          display_name: roleForm.display_name,
          description: roleForm.description,
        });
        await axios.put(rolePermissionsUrl(roleModal.role.id), { permissions: Array.from(rolePerms) });
        await load();
        setRoleModal(null);
        toastSuccess(`Role "${roleForm.display_name}" updated`);
      }
    } catch (err) {
      setRoleError(err.response?.data?.message || 'Failed');
    } finally { setRoleSaving(false); }
  };

  const deleteRole = async (role) => {
    if (role.role_key === 'super_admin') {
      return swalSuccess(
        'Super Administrator',
        'This role is required for system administration and cannot be deleted.'
      );
    }
    const counts = roleUserCounts.get(role.role_key) || { total: 0, active: 0 };
    const assigned = usersByRole.get(role.role_key) || [];
    const activeUsers = assigned.filter((u) => u.is_active === 1 || u.is_active === true);

    if (activeUsers.length > 0) {
      // Build a readable list of the people in the way so the admin knows
      // exactly whom to suspend (or reassign) before the role can be removed.
      const list = activeUsers.slice(0, 8).map((u) =>
        `<li><strong>${u.full_name}</strong> <span style="color: var(--mute)">— ${u.branch_name || 'no branch'}</span></li>`
      ).join('');
      const more = activeUsers.length > 8 ? `<li style="color: var(--mute)">…and ${activeUsers.length - 8} more</li>` : '';
      const html = `
        <p style="margin: 0 0 10px; color: var(--mute);">
          <strong>${activeUsers.length}</strong> active user${activeUsers.length === 1 ? '' : 's'} still ${activeUsers.length === 1 ? 'has' : 'have'} the role
          <strong>${role.display_name}</strong>. Suspend or reassign ${activeUsers.length === 1 ? 'them' : 'each of them'} from the Users tab, then try again.
        </p>
        <ul style="text-align: left; margin: 8px 0 0; padding-left: 22px; line-height: 1.7;">${list}${more}</ul>
      `;
      return swalWarning('Role is in use', '', { html, confirmButtonText: 'Got it' });
    }

    // No active users blocking — confirm and delete.
    // Stronger warning for built-in (system) roles since they're seeded as starting points.
    const suspendedCount = counts.total - counts.active;
    let text;
    if (role.is_system) {
      text = `"${role.display_name}" is a built-in role that ships with the system. Deleting it will remove it from the role dropdown when creating users. You can re-create it later as a custom role if needed.`;
      if (suspendedCount > 0) {
        text += ` It is currently held by ${suspendedCount} suspended user${suspendedCount === 1 ? '' : 's'}; they will keep the role label on their record.`;
      }
    } else {
      text = suspendedCount > 0
        ? `This role is currently held by ${suspendedCount} suspended user${suspendedCount === 1 ? '' : 's'}. They will keep the role label on their record, but the role itself will be removed from the system.`
        : 'This cannot be undone. The role will no longer be available when creating users.';
    }

    const okToDelete = await swalDangerConfirm(
      `Delete role "${role.display_name}"?`, text,
      { confirmButtonText: 'Yes, delete it' }
    );
    if (!okToDelete) return;
    try {
      await axios.delete(roleByIdUrl(role.id));
      await load();
      toastSuccess('Role deleted');
    } catch (err) {
      // Backend gave a 400 with the blockers list — pop the same informative modal.
      const data = err.response?.data;
      if (data?.code === 'ROLE_IN_USE' && Array.isArray(data.blockers)) {
        const list = data.blockers.slice(0, 8).map((u) =>
          `<li><strong>${u.full_name}</strong> <span style="color: var(--mute)">— ${u.username}</span></li>`
        ).join('');
        const more = data.blockers.length > 8 ? `<li style="color: var(--mute)">…and ${data.blockers.length - 8} more</li>` : '';
        swalWarning('Role is in use', '', {
          html: `<p style="margin: 0 0 10px; color: var(--mute);">${data.message}</p>
                 <ul style="text-align: left; margin: 8px 0 0; padding-left: 22px; line-height: 1.7;">${list}${more}</ul>`,
          confirmButtonText: 'Got it',
        });
      } else {
        showAxiosError(err, 'Could not delete role');
      }
    }
  };

  const togglePerm = (key) => {
    setRolePerms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleCategory = (cat) => {
    const list = permsByCategory[cat] || [];
    const allKeys = list.map((p) => p.perm_key);
    const allOn = allKeys.every((k) => rolePerms.has(k));
    setRolePerms((prev) => {
      const next = new Set(prev);
      if (allOn) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const submit = async () => {
    setError(null); setSaving(true);
    try {
      const payload = { ...form, branch_id: Number(form.branch_id) };
      if (!payload.branch_id) { setError('Choose a branch'); setSaving(false); return; }

      // Super admin can fine-tune permissions while creating the user.
      // Compute grants/revokes against the chosen role's defaults so the
      // backend stores only the override delta, not the full set.
      if (isSuper && userPermsTouched) {
        const r = roles.find((x) => x.role_key === form.role);
        const defaultSet = new Set(r?.permissions || []);
        const granted = [];
        const revoked = [];
        perms.forEach((p) => {
          const want = userPermsPick.has(p.perm_key);
          const fromRole = defaultSet.has(p.perm_key);
          if (want && !fromRole) granted.push(p.perm_key);
          if (!want && fromRole) revoked.push(p.perm_key);
        });
        payload.permissions_granted = granted;
        payload.permissions_revoked = revoked;
      }

      const res = await axios.post(usersUrl, payload);
      if (res.data?.success) {
        const temp = form.temp_password;
        const newUserId = res.data?.id;
        // Link fingerprint device if both fields were filled
        if (newUserId && form.device_serial && form.device_user_id) {
          try {
            await axios.post(attendanceLinkUrl, {
              user_id: newUserId,
              device_serial: form.device_serial,
              device_user_id: String(form.device_user_id),
            });
          } catch (_) { /* non-fatal — device link can be done later */ }
        }
        setOpen(false); setForm(emptyUser); setUserPermsTouched(false); setUserPermsPick(new Set()); await load();
        swalSuccess(
          'User created',
          `Share the temporary password with ${form.full_name} — they must change it on first sign-in.\n\nPassword: ${temp}`
        );
      } else setError(res.data?.message || 'Failed');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    const action = u.is_active ? 'suspend' : 'activate';
    const ok = await swalDangerConfirm(
      `${action === 'suspend' ? 'Suspend' : 'Activate'} ${u.full_name}?`,
      action === 'suspend'
        ? `${u.full_name} will be unable to sign in until you reactivate the account.`
        : `${u.full_name} will be able to sign in again.`,
      { confirmButtonText: action === 'suspend' ? 'Yes, suspend' : 'Yes, activate' }
    );
    if (!ok) return;
    try {
      await axios.patch(userStatusUrl(u.id), { is_active: !u.is_active });
      toastSuccess(`User ${u.is_active ? 'suspended' : 'activated'}`);
      load();
    } catch (err) { showAxiosError(err, 'Could not update user'); }
  };

  const resetPassword = async (u) => {
    const ok = await swalDangerConfirm(
      `Reset password for ${u.full_name}?`,
      'A new temporary password will be generated and the user will be forced to change it on next sign-in.',
      { confirmButtonText: 'Yes, reset' }
    );
    if (!ok) return;
    const temp = generatePassword();
    try {
      await axios.post(userResetPwdUrl(u.id), { temp_password: temp });
      swalSuccess(
        'Password reset',
        `Share this temporary password with ${u.full_name}:\n\n${temp}`
      );
    } catch (err) { showAxiosError(err, 'Could not reset password'); }
  };

  const openPerms = async (u) => {
    setPermModalUser(u);
    const res = await axios.get(userPermsUrl(u.id));
    if (res.data?.success) {
      setUserPerms(res.data);
      setPendingGrants(new Set(res.data.effective || []));
    }
  };

  const savePerms = async () => {
    const role = new Set(userPerms.role_permissions);
    const grants = [], revokes = [];
    perms.forEach((p) => {
      const want = pendingGrants.has(p.perm_key);
      const fromRole = role.has(p.perm_key);
      if (want && !fromRole) grants.push(p.perm_key);
      if (!want && fromRole) revokes.push(p.perm_key);
    });
    await axios.put(userPermsUrl(permModalUser.id), { granted: grants, revoked: revokes });
    toastSuccess(`Permissions updated for ${permModalUser.full_name}`);
    setPermModalUser(null);
  };

  const openDeviceModal = (u) => {
    setDeviceModal({ user: u });
    setDeviceForm({ device_serial: u.device_serial || '', device_user_id: u.device_user_id || '' });
    setDeviceError(null);
  };

  const saveDevice = async () => {
    setDeviceError(null);
    if (!deviceForm.device_serial) { setDeviceError('Select a device'); return; }
    if (!deviceForm.device_user_id) { setDeviceError('Enter the fingerprint terminal ID for this user'); return; }
    setDeviceSaving(true);
    try {
      const res = await axios.post(attendanceLinkUrl, {
        user_id: deviceModal.user.id,
        device_serial: deviceForm.device_serial,
        device_user_id: String(deviceForm.device_user_id),
      });
      if (res.data?.success) {
        toastSuccess(`Device linked for ${deviceModal.user.full_name}`);
        setDeviceModal(null);
        await load();
      } else {
        setDeviceError(res.data?.message || 'Failed to link device');
      }
    } catch (err) {
      setDeviceError(err.response?.data?.message || 'Failed to link device');
    } finally { setDeviceSaving(false); }
  };

  return (
    <>
      {isSuper && (
        <div className="ua-tabs">
          <button
            className={`ua-tab ${tab === 'users' ? 'active' : ''}`}
            onClick={() => setTab('users')}
          >
            <span className="ic">👥</span>
            Users
            <span className="ct">{users.length}</span>
          </button>
          <button
            className={`ua-tab ${tab === 'roles' ? 'active' : ''}`}
            onClick={() => setTab('roles')}
          >
            <span className="ic">🔑</span>
            Roles & Permissions
            <span className="ct">{roles.length}</span>
          </button>
        </div>
      )}

      {tab === 'users' && !isSuper && (
        <div className="notice info" style={{ marginBottom: 14 }}>
          <div>
            <strong>Access levels are managed centrally from head office.</strong>
            <div style={{ marginTop: 3, color: 'var(--mute)', fontSize: 12.5, lineHeight: 1.5 }}>
              You can create staff accounts at <strong>{branches.find((b) => b.id === me?.branch_id)?.name || 'your branch'}</strong> and pick from the roles the super administrator has published. What each role can actually do is defined there — you don't need to configure permissions yourself.
            </div>
          </div>
        </div>
      )}

      {tab === 'users' && (
      <div className="panel">
        <div className="panel-head">
          <h2>Users &amp; Access</h2>
          <div className="panel-toolbar">
            <input className="f-input" placeholder="Search users…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
            <button
              className="btn-primary"
              onClick={() => {
                setForm({
                  ...emptyUser,
                  branch_id: isSuper ? '' : (me?.branch_id || ''),
                  role: 'cashier',
                  temp_password: generatePassword(),
                });
                // Seed permission picker from the default role; admin can edit.
                const cashier = roles.find((r) => r.role_key === 'cashier');
                setUserPermsPick(new Set(cashier?.permissions || []));
                setUserPermsTouched(false);
                setUserPermFilter('');
                setUserPermCollapsed(new Set());
                setOpen(true);
                setError(null);
              }}
            >+ New user</button>
          </div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th><th>Username</th><th>Email</th><th>Branch</th>
                <th>Role</th><th>Fingerprint</th><th>Status</th><th>Last login</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id}>
                  <td><strong>{u.full_name}</strong></td>
                  <td>{u.username}</td>
                  <td>{u.email}</td>
                  <td>{u.branch_name || '—'}</td>
                  <td><span className="badge badge-gold">{titleCase(u.role)}</span></td>
                  <td>
                    {u.device_user_id
                      ? <span className="badge badge-green" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>ID {u.device_user_id}</span>
                      : <span className="badge" style={{ background: 'var(--bg-tint)', color: 'var(--mute)', fontSize: 11 }}>Not enrolled</span>}
                  </td>
                  <td>{u.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-red">Inactive</span>}</td>
                  <td>{fmtDate(u.last_login_at)}</td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    {isSuper && (
                      <button className="btn-link" onClick={() => openPerms(u)}>Permissions</button>
                    )}
                    <button className="btn-link" onClick={() => openDeviceModal(u)}>
                      {u.device_user_id ? '🖐 Device' : '🖐 Enroll'}
                    </button>
                    <button className="btn-link" onClick={() => resetPassword(u)}>Reset PW</button>
                    <button className="btn-link" onClick={() => toggleStatus(u)} style={{ color: u.is_active ? 'var(--danger)' : 'var(--success)' }}>
                      {u.is_active ? 'Suspend' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="9" style={{ color: 'var(--mute)', textAlign: 'center', padding: 24 }}>No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {tab === 'roles' && isSuper && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <h2>Roles &amp; Permissions</h2>
              <p className="muted-sm" style={{ margin: '2px 0 0' }}>
                Bundle permissions into named roles. Pick a role when creating a user and they inherit those rights automatically.
              </p>
            </div>
            <div className="panel-toolbar">
              <input
                className="f-input"
                placeholder="Search roles…"
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
                style={{ width: 240 }}
              />
              <button className="btn-primary" onClick={openCreateRole}>+ New role</button>
            </div>
          </div>

          <div className="role-grid">
            {filteredRoles.map((r) => {
              const isSuperRole = r.role_key === 'super_admin';
              const counts = roleUserCounts.get(r.role_key) || { total: 0, active: 0 };
              const permCount = isSuperRole ? perms.length : (r.permissions?.length || 0);
              const hasSuspended = counts.total > counts.active;
              const color = avatarFor(r.role_key);
              const pct = isSuperRole ? 100 : (perms.length ? Math.round((permCount / perms.length) * 100) : 0);
              return (
                <div key={r.id} className={`role-card ${r.is_system ? 'system' : 'custom'}`}>
                  <div className="role-card-top">
                    <div
                      className="role-avatar"
                      style={{ background: color.bg, color: color.fg }}
                      aria-hidden
                    >
                      {isSuperRole ? '★' : roleInitials(r.display_name)}
                    </div>
                    <div className="role-headline">
                      <h3>{r.display_name}</h3>
                      <div className="role-sub">
                        <span className={`role-pill ${r.is_system ? 'system' : 'custom'}`}>
                          {r.is_system ? 'System' : 'Custom'}
                        </span>
                        <code className="role-key">{r.role_key}</code>
                      </div>
                    </div>
                  </div>

                  <p className="role-desc">
                    {r.description || (isSuperRole
                      ? 'Implicit full access — every permission across the system.'
                      : <em style={{ color: 'var(--mute-2)' }}>No description.</em>)}
                  </p>

                  <div className="role-progress" aria-hidden>
                    <div className="role-progress-head">
                      <span>Permissions granted</span>
                      <strong>{permCount}{isSuperRole ? ' (all)' : ` / ${perms.length}`}</strong>
                    </div>
                    <div className="role-progress-bar">
                      <div className="fill" style={{ width: `${pct}%`, background: color.fg }} />
                    </div>
                  </div>

                  <div className="role-meta">
                    <div className="role-meta-stat">
                      <div className="ic">👥</div>
                      <div>
                        <div className="lbl">Users with this role</div>
                        <div className="val">
                          {counts.active}
                          {hasSuspended && <span className="sub"> · {counts.total - counts.active} suspended</span>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isSuperRole && (
                    <div className="role-perm-preview">
                      {(r.permissions || []).slice(0, 5).map((p) => (
                        <span key={p} className="perm-chip">{p}</span>
                      ))}
                      {(r.permissions?.length || 0) > 5 && (
                        <span className="perm-chip more">+{r.permissions.length - 5} more</span>
                      )}
                      {(r.permissions?.length || 0) === 0 && (
                        <span className="perm-chip empty">No permissions yet</span>
                      )}
                    </div>
                  )}

                  <div className="role-actions">
                    {!isSuperRole && (
                      <button className="role-edit-btn" onClick={() => openEditRole(r)}>
                        <span>✎</span> Edit &amp; permissions
                      </button>
                    )}
                    <button
                      className="role-delete-btn"
                      onClick={() => deleteRole(r)}
                      disabled={isSuperRole}
                      title={isSuperRole
                        ? 'Super Administrator is required by the system and cannot be deleted'
                        : 'Delete this role'}
                    >
                      <span>🗑</span> Delete
                    </button>
                  </div>
                </div>
              );
            })}
            {filteredRoles.length === 0 && (
              <div className="role-empty">
                <div className="ic">🔑</div>
                <h3>{roleSearch ? 'No roles match' : 'No roles yet'}</h3>
                <p>Roles let you bundle permissions so new users inherit access automatically.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create / edit role */}
      <Modal
        title={roleModal?.mode === 'edit' ? `Edit role — ${roleModal.role.display_name}` : 'Create new role'}
        open={!!roleModal} onClose={() => setRoleModal(null)} width={1080}
        footer={
          <>
            <div style={{ color: 'var(--mute)', fontSize: 13, marginRight: 'auto' }}>
              <strong style={{ color: 'var(--brand)' }}>{rolePerms.size}</strong> of {perms.length} permissions selected
            </div>
            <button className="btn-ghost" onClick={() => setRoleModal(null)}>Cancel</button>
            <button className="btn-primary" onClick={saveRole} disabled={roleSaving}>
              {roleSaving ? 'Saving…' : (roleModal?.mode === 'edit' ? 'Save changes' : 'Create role')}
            </button>
          </>
        }
      >
        {roleError && <div className="notice">{roleError}</div>}

        <div className="role-modal-layout">
          {/* LEFT — role basics */}
          <aside className="role-modal-aside">
            <div className="role-modal-avatar"
              style={{
                background: avatarFor(roleForm.role_key || 'new_role').bg,
                color: avatarFor(roleForm.role_key || 'new_role').fg,
              }}
            >
              {roleInitials(roleForm.display_name || 'New')}
            </div>
            <div className="role-modal-aside-label">
              {roleModal?.mode === 'edit' ? 'Editing role' : 'Defining new role'}
            </div>
            <div className="role-modal-aside-name">{roleForm.display_name || 'Untitled role'}</div>
            <code className="role-modal-aside-key">{roleForm.role_key || 'role_key'}</code>

            <div className="f-row" style={{ marginTop: 22 }}>
              <label className="f-label">Display name *</label>
              <input
                className="f-input"
                value={roleForm.display_name}
                onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                placeholder="e.g. Senior Cashier"
              />
            </div>
            <div className="f-row">
              <label className="f-label">
                Role key {roleModal?.mode === 'create' && '*'}
                {roleModal?.mode === 'edit' && <span style={{ color: 'var(--mute-2)', textTransform: 'none', fontWeight: 500, marginLeft: 6 }}>(locked)</span>}
              </label>
              <input
                className="f-input"
                value={roleForm.role_key}
                onChange={(e) => setRoleForm({ ...roleForm, role_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                placeholder="senior_cashier"
                disabled={roleModal?.mode === 'edit'}
              />
              {roleModal?.mode === 'create' && (
                <p style={{ fontSize: 11.5, color: 'var(--mute)', margin: '6px 0 0' }}>
                  Lowercase letters and underscores. Used internally.
                </p>
              )}
            </div>
            <div className="f-row" style={{ marginBottom: 0 }}>
              <label className="f-label">Description</label>
              <textarea
                className="f-textarea"
                rows={3}
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="What this role can do — short explanation"
                style={{ resize: 'vertical' }}
              />
            </div>

            {roleModal?.mode === 'create' && roles.length > 0 && (
              <div className="role-copy-from">
                <label className="f-label">Quick start — copy permissions from</label>
                <select
                  className="f-select"
                  value={copyFromRole}
                  onChange={(e) => applyCopyFrom(e.target.value)}
                >
                  <option value="">— Start empty —</option>
                  {roles.filter((r) => r.role_key !== 'super_admin').map((r) => (
                    <option key={r.role_key} value={r.role_key}>
                      {r.display_name} ({(r.permissions || []).length} perms)
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: 11.5, color: 'var(--mute)', margin: '6px 0 0' }}>
                  Picks the same permissions as the chosen role — you can then add or remove individually.
                </p>
              </div>
            )}
          </aside>

          {/* RIGHT — permission picker */}
          <section className="role-modal-perms">
            <div className="role-perms-head">
              <div>
                <h4>Permissions</h4>
                <p>Pick exactly what this role can do. Section headers toggle the whole category at once.</p>
              </div>
              <div className="role-perms-tools">
                <div className="role-perms-search">
                  <span className="ic">🔍</span>
                  <input
                    className="f-input"
                    placeholder="Filter permissions…"
                    value={permFilter}
                    onChange={(e) => setPermFilter(e.target.value)}
                  />
                  {permFilter && (
                    <button className="clear" onClick={() => setPermFilter('')} title="Clear filter">×</button>
                  )}
                </div>
                <button
                  type="button"
                  className="cat-collapse-all"
                  onClick={collapsedCats.size === Object.keys(permsByCategory).length ? expandAllCats : collapseAllCats}
                  title={collapsedCats.size === Object.keys(permsByCategory).length ? 'Expand all categories' : 'Collapse all categories'}
                >
                  {collapsedCats.size === Object.keys(permsByCategory).length ? '⤡ Expand all' : '⤢ Collapse all'}
                </button>
              </div>
            </div>

            <div className="role-perms-list">
              {Object.entries(permsByCategory).map(([cat, list]) => {
                const visible = list.filter(permFilterMatch);
                if (visible.length === 0) return null;
                const selectedInCat = list.filter((p) => rolePerms.has(p.perm_key)).length;
                const allOn = selectedInCat === list.length;
                // When the user is searching, force the matching category open
                // so they can see and click the matches.
                const isCollapsed = !permFilter.trim() && collapsedCats.has(cat);
                return (
                  <div key={cat} className={`perm-category ${isCollapsed ? 'collapsed' : ''}`}>
                    <div
                      className="perm-category-head"
                      onClick={() => toggleCatCollapse(cat)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCatCollapse(cat); } }}
                      title={isCollapsed ? 'Expand category' : 'Collapse category'}
                    >
                      <div className="cat-title">
                        <span className={`cat-chevron ${isCollapsed ? 'closed' : ''}`} aria-hidden>▾</span>
                        <span className="cat-ic">{catIcon(cat)}</span>
                        <h4>{titleCase(cat)}</h4>
                        <span className={`cat-count ${selectedInCat > 0 ? 'on' : ''}`}>
                          {selectedInCat} / {list.length}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="cat-toggle"
                        onClick={(e) => { e.stopPropagation(); toggleCategory(cat); }}
                      >
                        {allOn ? 'Clear all' : 'Select all'}
                      </button>
                    </div>
                    {!isCollapsed && (
                      <div className="perm-grid">
                        {visible.map((p) => (
                          <label
                            key={p.perm_key}
                            className={`perm-row ${rolePerms.has(p.perm_key) ? 'on' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={rolePerms.has(p.perm_key)}
                              onChange={() => togglePerm(p.perm_key)}
                            />
                            <div className="perm-row-body">
                              <span className="perm-name">{p.description || p.perm_key}</span>
                              <code className="perm-key">{p.perm_key}</code>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {Object.values(permsByCategory).every((list) => list.filter(permFilterMatch).length === 0) && (
                <div className="perm-empty">
                  <div className="ic">🔍</div>
                  <p>No permissions match "<strong>{permFilter}</strong>".</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </Modal>

      {/* Create user — two-column for super admin (with permission picker), single-column for branch manager */}
      <Modal
        title="Create user" open={open} onClose={() => setOpen(false)} width={isSuper ? 1080 : 680}
        footer={
          <>
            {isSuper && (
              <div style={{ color: 'var(--mute)', fontSize: 13, marginRight: 'auto' }}>
                {(() => {
                  const r = roles.find((x) => x.role_key === form.role);
                  const def = new Set(r?.permissions || []);
                  const extras = Array.from(userPermsPick).filter((k) => !def.has(k)).length;
                  const removed = perms.filter((p) => def.has(p.perm_key) && !userPermsPick.has(p.perm_key)).length;
                  return (
                    <>
                      <strong style={{ color: 'var(--brand)' }}>{userPermsPick.size}</strong> permissions
                      {(extras > 0 || removed > 0) && (
                        <span> · <span style={{ color: 'var(--success)' }}>+{extras}</span> / <span style={{ color: 'var(--danger)' }}>−{removed}</span> vs role</span>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Create user'}</button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}

        <div className={isSuper ? 'role-modal-layout' : ''}>
          {/* LEFT — user details */}
          <aside className={isSuper ? 'role-modal-aside' : ''}>
            {isSuper && (
              <>
                <div className="role-modal-aside-label">New user</div>
                <div className="role-modal-aside-name">{form.full_name || 'Unnamed'}</div>
                <code className="role-modal-aside-key">@{form.username || 'username'}</code>
              </>
            )}

            <div className="f-grid f-grid-2" style={isSuper ? { marginTop: 18 } : {}}>
              <div className="f-row">
                <label className="f-label">Full name *</label>
                <input className="f-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div className="f-row">
                <label className="f-label">Username *</label>
                <input className="f-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Email *</label>
                <input className="f-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Phone</label>
                <input className="f-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Branch *</label>
                {isSuper ? (
                  <select className="f-select" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                    <option value="">— Choose —</option>
                    {branches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.branch_code})</option>)}
                  </select>
                ) : (
                  <input
                    className="f-input"
                    value={(branches.find((b) => b.id === Number(form.branch_id))?.name) || 'Your branch'}
                    disabled
                    title="Branch managers can only create users in their own branch"
                  />
                )}
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Role *</label>
                <select
                  className="f-select"
                  value={form.role}
                  onChange={(e) => {
                    setForm({ ...form, role: e.target.value });
                    setUserPermsTouched(false); // role change re-seeds picker
                  }}
                >
                  {roles
                    .filter((r) => isSuper || BRANCH_MGR_ROLES.has(r.role_key))
                    .map((r) => <option key={r.role_key} value={r.role_key}>{r.display_name}</option>)}
                </select>
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Temporary password * <span style={{ textTransform: 'none', color: 'var(--mute)' }}>(user must change on first login)</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="f-input" value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} />
                  <button className="btn-ghost" type="button" onClick={() => setForm({ ...form, temp_password: generatePassword() })}>Generate</button>
                </div>
              </div>

              {/* ── Fingerprint device (optional) ── */}
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--line)', paddingTop: 14, marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--mute)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                  🖐 Fingerprint device <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional, can be set later</span>
                </div>
                <div className="f-grid f-grid-2">
                  <div className="f-row">
                    <label className="f-label">Fingerprint terminal</label>
                    <select className="f-select" value={form.device_serial} onChange={(e) => setForm({ ...form, device_serial: e.target.value })}>
                      <option value="">— Select device —</option>
                      {devices.map((d) => (
                        <option key={d.serial_number} value={d.serial_number}>
                          {d.name} {d.branch_name ? `(${d.branch_name})` : ''} · {d.serial_number}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="f-row">
                    <label className="f-label">Terminal user ID <span style={{ color: 'var(--mute)', textTransform: 'none', fontWeight: 400 }}>(number assigned by device)</span></label>
                    <input
                      className="f-input"
                      type="number"
                      min="1"
                      placeholder="e.g. 5"
                      value={form.device_user_id}
                      onChange={(e) => setForm({ ...form, device_user_id: e.target.value })}
                    />
                  </div>
                </div>
                {devices.length === 0 && (
                  <p style={{ fontSize: 12.5, color: 'var(--mute)', margin: '6px 0 0' }}>
                    No fingerprint terminals registered yet — go to <strong>Attendance → Devices</strong> to add one first.
                  </p>
                )}
              </div>
            </div>

            {!isSuper && (
              <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--brand-soft)', borderRadius: 10, border: '1px solid var(--brand-ring)' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 18 }}>🔑</span>
                  <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.55 }}>
                    <strong style={{ color: 'var(--brand)' }}>Role decides access.</strong>{' '}
                    Whatever <em>{roles.find((r) => r.role_key === form.role)?.display_name || form.role}</em> can do
                    across the system is defined by the super administrator — this user will inherit those rights automatically.
                    {' '}If they need more or fewer permissions, contact head office.
                  </div>
                </div>
              </div>
            )}
          </aside>

          {/* RIGHT — permission picker (super admin only) */}
          {isSuper && (
            <section className="role-modal-perms">
              <div className="role-perms-head">
                <div>
                  <h4>Permissions for this user</h4>
                  <p>Pre-filled from the chosen role. Tick to add extras, untick to remove the role's defaults — overrides save with the user.</p>
                </div>
                <div className="role-perms-tools">
                  <div className="role-perms-search">
                    <span className="ic">🔍</span>
                    <input
                      className="f-input"
                      placeholder="Filter permissions…"
                      value={userPermFilter}
                      onChange={(e) => setUserPermFilter(e.target.value)}
                    />
                    {userPermFilter && (
                      <button className="clear" onClick={() => setUserPermFilter('')} title="Clear filter">×</button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="cat-collapse-all"
                    onClick={() => {
                      const allCollapsed = userPermCollapsed.size === Object.keys(permsByCategory).length;
                      setUserPermCollapsed(allCollapsed ? new Set() : new Set(Object.keys(permsByCategory)));
                    }}
                  >
                    {userPermCollapsed.size === Object.keys(permsByCategory).length ? '⤡ Expand all' : '⤢ Collapse all'}
                  </button>
                </div>
              </div>

              <div className="role-perms-list">
                {Object.entries(permsByCategory).map(([cat, list]) => {
                  const visible = list.filter((p) => {
                    if (!userPermFilter.trim()) return true;
                    const q = userPermFilter.trim().toLowerCase();
                    return p.perm_key.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
                  });
                  if (visible.length === 0) return null;
                  const r = roles.find((x) => x.role_key === form.role);
                  const defaultSet = new Set(r?.permissions || []);
                  const selectedInCat = list.filter((p) => userPermsPick.has(p.perm_key)).length;
                  const allOn = selectedInCat === list.length;
                  const isCollapsed = !userPermFilter.trim() && userPermCollapsed.has(cat);
                  return (
                    <div key={cat} className={`perm-category ${isCollapsed ? 'collapsed' : ''}`}>
                      <div
                        className="perm-category-head"
                        onClick={() => {
                          setUserPermCollapsed((prev) => {
                            const next = new Set(prev);
                            if (next.has(cat)) next.delete(cat); else next.add(cat);
                            return next;
                          });
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="cat-title">
                          <span className={`cat-chevron ${isCollapsed ? 'closed' : ''}`} aria-hidden>▾</span>
                          <span className="cat-ic">{catIcon(cat)}</span>
                          <h4>{titleCase(cat)}</h4>
                          <span className={`cat-count ${selectedInCat > 0 ? 'on' : ''}`}>
                            {selectedInCat} / {list.length}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="cat-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserPermsTouched(true);
                            setUserPermsPick((prev) => {
                              const next = new Set(prev);
                              const allKeys = list.map((p) => p.perm_key);
                              const isAllOn = allKeys.every((k) => next.has(k));
                              if (isAllOn) allKeys.forEach((k) => next.delete(k));
                              else allKeys.forEach((k) => next.add(k));
                              return next;
                            });
                          }}
                        >
                          {allOn ? 'Clear all' : 'Select all'}
                        </button>
                      </div>
                      {!isCollapsed && (
                        <div className="perm-grid">
                          {visible.map((p) => {
                            const checked = userPermsPick.has(p.perm_key);
                            const fromRole = defaultSet.has(p.perm_key);
                            const overrideStatus = checked && !fromRole ? 'granted'
                              : !checked && fromRole ? 'revoked' : null;
                            return (
                              <label
                                key={p.perm_key}
                                className={`perm-row ${checked ? 'on' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    setUserPermsTouched(true);
                                    setUserPermsPick((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(p.perm_key)) next.delete(p.perm_key);
                                      else next.add(p.perm_key);
                                      return next;
                                    });
                                  }}
                                />
                                <div className="perm-row-body">
                                  <span className="perm-name">
                                    {p.description || p.perm_key}
                                    {overrideStatus === 'granted' && <span className="perm-tag grant">+ extra</span>}
                                    {overrideStatus === 'revoked' && <span className="perm-tag revoke">− removed</span>}
                                    {fromRole && !overrideStatus && <span className="perm-tag inherit">from role</span>}
                                  </span>
                                  <code className="perm-key">{p.perm_key}</code>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </Modal>

      {/* Device / fingerprint enrollment */}
      <Modal
        title={deviceModal ? `Fingerprint device — ${deviceModal.user.full_name}` : 'Fingerprint device'}
        open={!!deviceModal} onClose={() => setDeviceModal(null)} width={520}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setDeviceModal(null)}>Cancel</button>
            <button className="btn-primary" onClick={saveDevice} disabled={deviceSaving}>
              {deviceSaving ? 'Linking…' : 'Link device'}
            </button>
          </>
        }
      >
        {deviceModal && (
          <>
            <p style={{ color: 'var(--mute)', marginTop: 0, fontSize: 13.5, lineHeight: 1.55 }}>
              Assign a fingerprint terminal and the numeric ID this person was enrolled under on that device.
              Once linked, their punches will appear under their name in Attendance automatically.
            </p>
            {deviceError && <div className="notice">{deviceError}</div>}
            <div className="f-row">
              <label className="f-label">Fingerprint terminal *</label>
              {devices.length > 0 ? (
                <select
                  className="f-select"
                  value={deviceForm.device_serial}
                  onChange={(e) => setDeviceForm({ ...deviceForm, device_serial: e.target.value })}
                >
                  <option value="">— Select device —</option>
                  {devices.map((d) => (
                    <option key={d.serial_number} value={d.serial_number}>
                      {d.name} {d.branch_name ? `(${d.branch_name})` : ''} · {d.serial_number}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ padding: '10px 14px', background: 'var(--bg-tint)', borderRadius: 8, fontSize: 13, color: 'var(--mute)' }}>
                  No fingerprint terminals registered. Go to <strong>Attendance → Devices</strong> to add one first.
                </div>
              )}
            </div>
            <div className="f-row">
              <label className="f-label">Terminal user ID * <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--mute)' }}>(number assigned by device during fingerprint enrolment)</span></label>
              <input
                className="f-input"
                type="number"
                min="1"
                placeholder="e.g. 5"
                value={deviceForm.device_user_id}
                onChange={(e) => setDeviceForm({ ...deviceForm, device_user_id: e.target.value })}
              />
            </div>
            {deviceModal.user.device_user_id && (
              <div style={{ marginTop: 8, padding: '10px 14px', background: 'var(--brand-soft)', borderRadius: 8, fontSize: 12.5, color: 'var(--ink)', border: '1px solid var(--brand-ring)' }}>
                Currently enrolled as ID <strong>{deviceModal.user.device_user_id}</strong> on device <code style={{ fontSize: 11 }}>{deviceModal.user.device_serial}</code>.
                Saving will update to the new values above.
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Permissions */}
      <Modal
        title={permModalUser ? `Permissions — ${permModalUser.full_name}` : 'Permissions'}
        open={!!permModalUser} onClose={() => setPermModalUser(null)} width={760}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setPermModalUser(null)}>Close</button>
            <button className="btn-primary" onClick={savePerms}>Save permissions</button>
          </>
        }
      >
        {permModalUser && (
          <>
            <p style={{ color: 'var(--mute)', marginTop: 0 }}>
              Role <strong>{titleCase(permModalUser.role)}</strong> grants a default set. Toggle any extra you want to grant or remove for this specific user.
            </p>
            {Object.entries(permsByCategory).map(([cat, list]) => (
              <div key={cat} className="perm-category">
                <h4>{titleCase(cat)}</h4>
                {list.map((p) => (
                  <label key={p.perm_key} className="perm-row">
                    <input
                      type="checkbox"
                      checked={pendingGrants.has(p.perm_key)}
                      onChange={(e) => {
                        const next = new Set(pendingGrants);
                        if (e.target.checked) next.add(p.perm_key); else next.delete(p.perm_key);
                        setPendingGrants(next);
                      }}
                    />
                    <span style={{ flex: 1 }}>{p.description || p.perm_key}</span>
                    {userPerms.role_permissions.includes(p.perm_key) && <span className="badge badge-gold">via role</span>}
                  </label>
                ))}
              </div>
            ))}
          </>
        )}
      </Modal>
    </>
  );
};

export default UsersAccess;
